import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { ArcGISDrawingInfo, ArcGISLayerData } from './arcgis-types';

export interface GisServiceList {
    serviceId?: number;
    serviceType: ServiceTypes;
    url: string;
    appId?: string;
    authURL?: string;
    authparam: string;
    customerId: number;
    authParams: string;
    authMethod: string;
    userName: string;
    password: string;
    hasIdentityConfig?: boolean;
}

export interface GISServiceLayerList {
    name: string;
    title: string;
    customerId: number;
}

export interface GISLayerMapping {
    id: number;
    customerId: number;
    structureType: string;
    service: string;
    layer: string;
    attribute1: string;
    attribute2: string;
}

export interface BaseMapListItem {
    baseMapId: string;
    url: string;
    name: string;
    imageURL: string;
    mapKey: string;
    isActive: boolean;
}

export interface gisUserSettings {
    id: number;
    userId: number;
    locationsIsActive: boolean;
    languageSelection: string;
    sidebarIsOn: boolean;
    baseMapId: string;
    isMapPanelOn: boolean;
    sidebarIsOn2: boolean;
    isLayersOn: boolean;
    isActive: boolean;
    selectedLocationGroupId: number;
    locationPositionX: string;
    locationPositionY: string;
    isLayersSelectable: boolean;
    activeLayersTabNo: number;
    isSidebarPinOn: boolean;
    isSidebarPinOn2: boolean;
    layersParameterJson: string;
    showLocationLabels: boolean;
    customerSettings: userSettingCustomerDetails[];
    lastViewedCustomerId: number;
    _etag?: string; // returned from server but need to be able to access and delete it!
}

export interface locationSearchOption {
    id: number;
    name: string;
}

export interface MapLayerLegendInfo {
    label: string;
    url?: string | unknown; // Can be string or SafeResourceUrl
    color?: string;
    borderColor?: string;
    type?: string | number; // LegendType enum value or string identifier
    height?: string;
}

export interface mapLayerList {
    checked: boolean;
    name: string;
    selection: boolean;
    showLegend?: boolean;
    legendInfo: MapLayerLegendInfo[];
    opacity: number;
    isArcGIS?: boolean;
    layerData?: ArcGISFeatureLayer | ArcGISWebMap | ArcGISLayerData | null;
    assetId?: string;
    assetType?: string;
    child?: mapLayerList[];
    showLabels?: boolean;
    labelField?: string;
}

export interface PrismLayerItem {
    name: string;
    checked: boolean;
    child: PrismLayerItem[];
    selection: boolean;
    legend?: boolean;
    legendurl?: string;
    opacity?: number;
    legendInfo?: MapLayerLegendInfo[];
}

export interface OLFeatureExtended extends Feature<Geometry> {
    values_?: Record<string, unknown>;
}

export interface featureSelList {
    feature: OLFeatureExtended | null;
    name: string;
    layername: string;
}

export interface userSettingCustomerDetails {
    cid: number;
    lastViewedExtents: number[];
    lastViewedDashboardConfig?: string;
    lastViewedLocationGroup: number;
    layers: userSettingLayerDetails[];
}

export interface userSettingLayerDetails {
    name: string;
    isVisible: boolean;
    isSelectable: boolean;
}
export enum ServiceTypes {
    WMS = 'wms',
    WFS = 'wfs',
    MAPS = 'maps',
    FEATURE = 'feat',
    IDENTITY = 'identity',
}

export interface GisToken {
    access_token: string;
    expires_in: number;
}

export interface ArcGISUserToken {
    userToken: string;
    tokenExpiresUtc: Date;
    refreshToken: string;
    refreshTokenExpiresUtc: Date;
}

export interface CreateArcGISUserTokenRequest {
    arcGISUserToken?: string;
    arcGISUserRefreshToken?: string;
    arcGISUserTokenExpiration?: string;
    arcGISUserRefreshTokenExpiration?: string;
    arcGISEnterpriseDomainUrl?: string | null;
    arcGISEnterpriseAppId?: string | null;
}

export interface GetArcGISUserTokenRequest {
    arcGISEnterpriseDomainUrl?: string | null;
    arcGISEnterpriseAppId?: string | null;
    customerId?: number;
}

/**
 * Need to define type by ourself.
 * It's ANY in REST package
 * https://github.com/Esri/arcgis-rest-js/blob/3172f1d8e0bc8e877c4489ed4c951f26775c8bb3/packages/arcgis-rest-feature-service/src/helpers.ts#L691C3-L691C14
 */
export interface FeatureServerDrawingInfo extends ArcGISDrawingInfo {
    label?: string;
}

export interface ArcGISFeatureService {
    id: string;
    title: string;
    type: string;
    url: string;
    access: string;
    owner?: string;
    created?: number;
    modified?: number;
    name?: string;
    geometryType?: string;
    serviceUrl?: string;
    thumbnail?: string;
    opacity?: number;
    contentStatus?: string;
    origin?: string;
    orgId?: string;
}

export interface ArcGISFeatureLayer {
    id: number;
    name: string;
    type: string;
    geometryType: string;
    minScale?: number;
    maxScale?: number;
    serviceUrl: string;
    url?: string;
    webMapId?: string;
    thumbnail?: string;
}

export interface ArcGISInternalLayer {
    id: number | string;
    name: string;
    url: string;
    serviceUrl: string;
    parentId?: string;
    parentName?: string;
    parentType?: string;
    checked?: boolean;
    geometryType?: string;
    minScale?: number;
    maxScale?: number;
    defaultVisibility?: boolean;
    capabilities?: string;
    fields?: unknown[];
    drawingInfo?: unknown;
    webMapId?: string;
    layerType?: string;
    opacity?: number;
    visibility?: boolean;
    layerDefinition?: unknown;
    popupInfo?: unknown;
    featureCollection?: unknown;
    displayField?: string;
    labelField?: string;
}

export interface ArcGISLayerDrawingInfo {
    renderer: {
        symbol?: {
            type: string;
            imageData?: string;
            contentType?: string;
            width?: number;
            height?: number;
        };
    };
}

export interface FetchFeatureServicesRequest {
    username?: string;
    orgId?: string;
    searchType: 'user' | 'organization' | 'both';
}

export interface ArcGISWebMap {
    id: string;
    title: string;
    type: 'Web Map';
    url?: string;
    snippet?: string;
    tags?: string[];
    owner: string;
    created: number;
    modified: number;
    access: 'private' | 'org' | 'public';
    thumbnail?: string;
    extent?: number[][];
    contentStatus?: string;
    origin?: string;
    orgId?: string;
}

export interface ArcGISMapService {
    id: string;
    title: string;
    type: string;
    url: string;
    access: string;
    owner?: string;
    created?: number;
    modified?: number;
    name?: string;
    thumbnail?: string;
    opacity?: number;
    contentStatus?: string;
    origin?: string;
    orgId?: string;
}

export interface ArcGISOrganizationContent {
    featureServices: ArcGISFeatureService[];
    webMaps: ArcGISWebMap[];
    mapServices: ArcGISMapService[];
    totalFeatureServices: number;
    totalWebMaps: number;
    totalMapServices: number;
    organizationId: string;
    organizationName?: string;
}

export interface ArcGISSearchResponse {
    total: number;
    start: number;
    num: number;
    nextStart: number;
    results: Array<ArcGISFeatureService | ArcGISWebMap | ArcGISMapService>;
}

export interface ArcGISPaginatedSearchResult {
    assets: Array<ArcGISFeatureService | ArcGISWebMap | ArcGISMapService>;
    total: number;
    nextStart: number;
    hasMore: boolean;
    searchTerm: string;
}

export interface ArcGISSearchParams {
    orgId: string;
    searchTerm?: string;
    start?: number;
    pageSize?: number;
}

export interface GetLayerRenderer {
  type: string;
  field1: string;
  valueExpression?: string;
  valueExpressionTitle?: string;
  defaultSymbol: GetLayerSymbol;
  defaultLabel: string;
  fieldDelimiter: string;
  authoringInfo: GetLayerAuthoringInfo;


  uniqueValueGroups?: GetLayerUniqueValueGroup[];
  uniqueValueInfos?: GetLayerUniqueValueInfo[];
  symbol?: GetLayerSymbol;
}

export interface GetLayerSymbol {
  type: string;
  style: string;
  imageData?: string;
  color: number[];
  width?: number;
  height?: number;
  marker?: GetLayerMarker;
  outline: GetLayerOutline;
}

export interface GetLayerMarker {
    placement: string;
    style: string;
}

export interface GetLayerOutline {
  color: number[];
  style?: string;
  type?: string;
  width?: number;
}

export interface GetLayerUniqueValueGroup {
  heading: string;
  classes: GetLayerClass[];
}

export interface GetLayerClass {
  label: string;
  description: string;
  symbol: GetLayerSymbol;
  values: string[][];
}

export interface GetLayerUniqueValueInfo {
  symbol: GetLayerSymbol
  value: string
  label: string
}

export interface GetLayerAuthoringInfo {
  colorRamp: GetLayerSymbolColorRamp
}

export interface GetLayerSymbolColorRamp {
  type: string
  colorRamps: GetLayerSymbolColorRampRow[]
}

export interface GetLayerSymbolColorRampRow {
  type: string
  algorithm: string
  fromColor: number[]
  toColor: number[]
}

export interface GISCoordinates {
    lat: number;
    lon: number;
}

export interface GISServiceTokenResponse {
    token: string;
}

/** conversion units from Scale (esri) to Resolution (openLayers) */
export const ESRI_INCHES_PER_METER = 39.3701;
/** conversion units from Scale (esri) to Resolution (openLayers) */
export const ESRI_DOTS_PER_INCH = 72;

/** Extra property to hold info about extend for "Zoom to Layer" function */
export const ESRI_VECTOR_LAYER_EXTENT_PROPERTY = 'esriExtent';

export interface GISServiceRequest {
    serviceType: ServiceTypes;
    URL: string;
    appId?: string;
    authUrl: string;
    AuthDetails: string;
    CustomerId: number;
    AuthMethod: string;
    UserName: string;
    Password: string;
    authParams: string;
}

export interface GISServiceUpdateRequest extends GISServiceRequest {
    serviceId: number;
}

export function isLegendFeatureServerDrawingInfo(param: FeatureServerDrawingInfo | FeatureServerLegendArrayItem[]): param is FeatureServerDrawingInfo {
    return !!param && !Array.isArray(param) && !!param.renderer;
}

export interface LayerAttributes {
    layerType: string;
    minScale: number;
    maxScale: number;
    legend: FeatureServerDrawingInfo | FeatureServerLegendArrayItem[];
    layerId?: number;
}

export interface LegendInfoResponse {
    layers: LayerAttributes[];
}


export interface FeatureServerLegendArrayItem {
    contentType: string;
    groupId: string;
    height: number;
    imageData: string;
    label: string;
    url: string;
    width: number;
    symbol: string;
}
